'use client';
import { useEffect, useState } from 'react';
import {
  SHORT_LINK_PATH_SEGMENT,
  parseShortLinkCode,
} from '@cabal/shared/business-logic/domains/short-link/short-link.url.js';
import { apiPost } from '@/lib/apiClient';

/**
 * Swaps a CABAL short link for the URL it was minted from, before the anchor is
 * clicked.
 *
 * The redirect route would work on its own, but going through it in-app costs a
 * hop and — the part that matters — leaves the browser status bar and the
 * right-click "copy link" showing our redirect instead of the real destination.
 * For a deliverable whose whole claim is that the booking link is real, the
 * destination has to be the thing the user can see and copy.
 *
 * Anything that is not a short link returns unchanged and never touches the
 * network, so ordinary markdown links cost nothing.
 */

// Under `/api`, not at the root where the redirect lives. The root path only
// reaches BL where the edge routes it there, and when it does not the request
// lands on the front end itself and 404s — which fails silently below and leaves
// every research link showing the mint origin instead of its destination.
// `/api/*` is the prefix every environment already points at BL.
const RESOLVE_PATH = `/api/${SHORT_LINK_PATH_SEGMENT}/resolve`;

interface ResolveResponse {
  readonly resolved?: Record<string, string>;
}

// One request per paint, not one per anchor: a shortlist renders dozens of
// links in the same tick, and a fetch each would be a request storm on every
// content render.
const pending = new Set<string>();
const resolved = new Map<string, string>();
const waiting = new Set<() => void>();
let scheduled = false;

function scheduleFlush(): void {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(() => {
    scheduled = false;
    void flush();
  });
}

async function flush(): Promise<void> {
  const urls = [...pending];
  if (urls.length === 0) return;
  pending.clear();
  try {
    const body = await apiPost<ResolveResponse>(RESOLVE_PATH, { urls });
    for (const [shortUrl, originalUrl] of Object.entries(body.resolved ?? {})) {
      resolved.set(shortUrl, originalUrl);
    }
  } catch {
    // Deliberately silent. An unresolved link keeps the short URL as its href
    // and still reaches the destination through the redirect route, so a failed
    // resolve costs a hop rather than a broken anchor.
  }
  for (const notify of [...waiting]) notify();
}

export function useResolvedShortLink(href: string): string {
  const isShortLink = parseShortLinkCode({ url: href }) !== null;
  // The cache is the source of truth and is readable during render, so the
  // href is DERIVED rather than mirrored into state. `arrivals` exists only to
  // re-render when the batch lands — the effect subscribes, it never computes.
  const [, setArrivals] = useState(0);
  const cached = isShortLink ? resolved.get(href) : undefined;

  useEffect(() => {
    if (!isShortLink || resolved.has(href)) return;
    let live = true;
    const notify = (): void => {
      if (live && resolved.has(href)) setArrivals((count) => count + 1);
    };
    waiting.add(notify);
    pending.add(href);
    scheduleFlush();
    return () => {
      live = false;
      waiting.delete(notify);
    };
  }, [href, isShortLink]);

  return cached ?? href;
}
